import { getErrorMessage, sortEmployeesByPriority } from 'src/lib/utils';
import { useNotificationStore } from '@dynamon-as/ui';
import axios from 'axios';
import { DEPARTMENT_PATH } from './config';
import { EmployeeService } from './EmployeeService';
import { ShiftTypeService } from './ShiftTypeService';
import { createAuthInterceptor } from 'src/features/auth/utils';

const dynoape = axios.create({
    baseURL: import.meta.env.VITE_SERVER_URL,
    withCredentials: true,
});

const rawDynoape = axios.create({
    baseURL: import.meta.env.VITE_SERVER_URL,
    withCredentials: true,
});

const rawDynoapeAPI = {
    get: async (url) => {
        try {
            return await rawDynoape.get(url);
        } catch (err) {
            return err;
        }
    },
};

const getCsrf = async () => {
    try {
        const response = await dynoape
            .get(`${import.meta.env.VITE_SERVER_URL}/csrf`)
            .catch((error) => {
                console.error('Error fetching CSRF token:', error);
                throw error;
            });
        dynoape.defaults.headers.common['X-XSRF-TOKEN'] = response.data.token;
        dynoape.defaults.withCredentials = true;
        return response.data.token;
    } catch (error) {
        console.log('Error fetching CSRF token:', error);
        throw error;
    }
};

const dynoapeAPI = {
    getCsrf: async () => await getCsrf(),
    get: async (url) => {
        const response = await dynoape.get(url).catch((error) => {
            console.error(error);
            return [];
        });
        return response?.data?.body;
    },
    // Get dynomite object from API. This is not suitable for all API responses.
    // TODO: For a proper generic establishment, the functionality would have to
    // be more robust.
    getDynomiteObj: async (url, objParser) => {
        const response = await dynoape
            .get(url, {
                responseType: 'text',
                transformResponse: [
                    (data) => {
                        const startMarker = '{"body":{';
                        // Any response from API will end with status.
                        // We don't need to know what is the status, only
                        // that size of it's encoding, that will be always
                        // the same.
                        const endMarkerLength = ',"status":XXX}'.length;

                        // Validate envelope
                        if (!data.startsWith(startMarker)) {
                            throw new Error(
                                'Invalid JSON envelope format: missing structural markers',
                            );
                        }

                        try {
                            const dynObj = objParser(
                                data.slice(
                                    startMarker.length - 1,
                                    -endMarkerLength,
                                ),
                            );
                            return { body: dynObj };
                        } catch (error) {
                            console.error(
                                'Decoding failed:',
                                error?.payload ? error.payload : error,
                            );
                            throw error;
                        }
                    },
                ],
            })
            .catch((error) => {
                console.error(error);
                return [];
            });
        return response?.data?.body;
    },
    post: async (url, data) => {
        await getCsrf();
        const response = await dynoape.post(url, data).catch((error) => {
            console.error(error);
            return [];
        });
        return response?.data?.body;
    },
    put: async (url, data) => {
        await getCsrf();
        const response = await dynoape.put(url, data).catch((error) => {
            console.error(error);
            return [];
        });
        return response?.data?.body;
    },
    emPatch: async (url, data) => {
        await getCsrf();
        const response = await dynoape.patch(url, data).catch((error) => {
            console.error(error);
            return [];
        });
        return response?.data?.body;
    },
    patch: async (url, data) => {
        await getCsrf();
        const response = await dynoape
            .patch(url, data, {
                headers: {
                    'Content-Type': 'application/json-patch+json',
                },
            })
            .catch((error) => {
                console.error(error);
                return [];
            });
        return response?.data?.body;
    },
    delete: async (url) => {
        await getCsrf();
        const response = await dynoape.delete(url);
        return response?.data?.body;
    },
};

const getSortedEmployees = async (departmentId) => {
    const employees = await EmployeeService.getEmployees(departmentId);
    if (!employees) {
        return [];
    }
    return sortEmployeesByPriority(employees);
};

const getSortedPositions = async (departmentId) => {
    const pos = await dynoapeAPI.get(
        `${DEPARTMENT_PATH}/${departmentId}/positions`,
    );
    if (!pos) {
        return [];
    }
    return pos.sort((a, b) =>
        a.name.localeCompare(b.name, 'no', {
            numeric: true,
            sensitivity: 'base',
        }),
    );
};

const getSortedCompetences = async (departmentId) => {
    const comp = await dynoapeAPI.get(
        `${DEPARTMENT_PATH}/${departmentId}/traits`,
    );
    if (!comp) {
        return [];
    }
    return comp.sort((a, b) =>
        a.name.localeCompare(b.name, 'no', {
            numeric: true,
            sensitivity: 'base',
        }),
    );
};

const getSortedShiftTypes = async (departmentId) => {
    const shiftTypes = await ShiftTypeService.getShiftTypes(departmentId);
    if (!shiftTypes) {
        return [];
    }
    return shiftTypes.sort((a, b) =>
        a.code.localeCompare(b.code, 'no', {
            numeric: true,
            sensitivity: 'base',
        }),
    );
};

const addAccessTokenInterceptor = (getAccessTokenSilently) => {
    [dynoape, rawDynoape].forEach((api) => {
        api.interceptors.request.use(
            createAuthInterceptor(getAccessTokenSilently),
        );
    });
};

const setupErrorInterceptor = () => {
    dynoape.interceptors.response.use(
        (response) => response,
        (error) => {
            const message = getErrorMessage(error.response);
            if (message.length > 0) {
                useNotificationStore.getState().addNotification({
                    type: 'error',
                    content: message,
                    position: 'top-right',
                });
            }
            return Promise.reject(error);
        },
    );
};

const taskTimeoutMinutes = import.meta.env.VITE_TASK_TIMEOUT_MINUTES ?? 1440;

export {
    dynoape,
    dynoapeAPI,
    getSortedEmployees,
    getSortedPositions,
    getSortedCompetences,
    taskTimeoutMinutes,
    addAccessTokenInterceptor,
    setupErrorInterceptor,
    getSortedShiftTypes,
    rawDynoapeAPI,
    rawDynoape,
};
